import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

interface MonthlySavingsForecastChartProps {
  data: Array<{
    month: string;
    historical_estimated: number;
    historical_released: number;
    forecast_estimated: number;
    forecast_released: number;
    confidence_level: number;
  }>;
  loading?: boolean;
}

const MonthlySavingsForecastChart: React.FC<MonthlySavingsForecastChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Savings Forecast</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Savings Forecast</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No forecast data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Find the split point between historical and forecast data
  const currentDate = new Date();
  const splitIndex = data.findIndex(item => new Date(item.month) > currentDate);
  const isForecast = splitIndex !== -1;

  const historicalData = isForecast ? data.slice(0, splitIndex) : data;
  const forecastData = isForecast ? data.slice(splitIndex) : [];

  const totalHistorical = historicalData.reduce((sum, item) => sum + item.historical_released, 0);
  const totalForecast = forecastData.reduce((sum, item) => sum + item.forecast_released, 0);
  const avgConfidence = forecastData.length > 0 ? 
    forecastData.reduce((sum, item) => sum + item.confidence_level, 0) / forecastData.length : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Savings Forecast</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
              domain={[0, 'dataMax + 10000']}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                name === 'historical_estimated' ? 'Historical Estimated' :
                name === 'historical_released' ? 'Historical Released' :
                name === 'forecast_estimated' ? 'Forecast Estimated' :
                name === 'forecast_released' ? 'Forecast Released' :
                name
              ]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Legend />
            {isForecast && (
              <ReferenceLine 
                x={data[splitIndex]?.month} 
                stroke="#ef4444" 
                strokeDasharray="5 5" 
                label="Forecast Start"
              />
            )}
            <Line
              type="monotone"
              dataKey="historical_estimated"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Historical Estimated"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="historical_released"
              stroke="#10b981"
              strokeWidth={3}
              name="Historical Released"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="forecast_estimated"
              stroke="#8b5cf6"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Forecast Estimated"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="forecast_released"
              stroke="#f59e0b"
              strokeWidth={3}
              name="Forecast Released"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 p-3 rounded-lg">
          <h4 className="font-medium text-green-900 mb-1">Historical Performance</h4>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalHistorical)}</p>
          <p className="text-sm text-green-700">Total released savings</p>
        </div>
        <div className="bg-orange-50 p-3 rounded-lg">
          <h4 className="font-medium text-orange-900 mb-1">Forecast Target</h4>
          <p className="text-2xl font-bold text-orange-600">{formatCurrency(totalForecast)}</p>
          <p className="text-sm text-orange-700">Predicted future savings</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <h4 className="font-medium text-purple-900 mb-1">Confidence Level</h4>
          <p className="text-2xl font-bold text-purple-600">{avgConfidence.toFixed(1)}%</p>
          <p className="text-sm text-purple-700">Forecast reliability</p>
        </div>
      </div>
      {isForecast && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Forecast Insights</h4>
          <div className="text-sm text-blue-700">
            <p>• Forecast based on historical trends and current workshop pipeline</p>
            <p>• Confidence level indicates reliability of predictions</p>
            <p>• Monitor actual vs. forecast performance for model accuracy</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlySavingsForecastChart;


